import { useRef, useState } from "react";
import {
  ALWAYS_ON,
  FEATURE_AREAS,
  FEATURES,
  type FeatureId,
  type GuestListScope,
  DEFAULT_MFA_RULE,
  DEFAULT_ORDER_LIMITS,
  DEFAULT_STANDARD_POURS,
  DEFAULT_TABLET_POLICY,
  IDLE_CHOICES,
  MAX_STANDARD_POURS,
  money,
  toCents,
  type MfaRule,
  type StandardPour,
  type TabletPolicy,
} from "@baros/core";
import { useApp } from "../context.js";
import { hasFeature } from "../features.js";
import type { OrgSettings, SettingsBody } from "./api.js";

/** The owner's choices for two-step sign-in (#92), in words. */
const MFA_CHOICES: readonly { readonly rule: MfaRule; readonly label: string }[] = [
  { rule: "off", label: "Nobody has to — anyone may turn it on for themselves" },
  { rule: "admins", label: "Owners and admins" },
  { rule: "everyone", label: "Everyone who signs in with a password" },
];

interface Props {
  readonly orgName: string;
  readonly settings: OrgSettings;
  readonly busy: string | null;
  readonly onSave: (body: SettingsBody) => Promise<void>;
}

/** The roles that carry an order approval limit — §4. */
const LIMIT_ROLES = ["bartender", "manager", "owner"] as const;

/** Cents in the API, dollars in the fields. The conversion lives here only. */
const toDollars = (cents: number) => (cents / 100).toFixed(2);

/** A unique-enough client-side id for tracking one chip across edits. */
function newTabId(): string {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * A prep-tab chip, remembering the name it had when the screen loaded.
 *
 * `original` is what makes a rename a rename rather than a drop-and-add: as
 * long as this chip keeps its `id`, editing `label` is understood as "this
 * tab, renamed", and `original` is sent as `prepTabRenames[].from` on save.
 * A chip added in this session has `original: null` — there is nothing to
 * rename, it is just a new tab.
 */
interface PrepTabDraft {
  readonly id: string;
  readonly label: string;
  readonly original: string | null;
}

function prepTabsOf(tabs: readonly string[]): PrepTabDraft[] {
  return tabs.map((label) => ({ id: newTabId(), label, original: label }));
}

/** A row of the standard-pours table as it is being typed: the amount is text until saved. */
interface PourDraft {
  readonly id: string;
  readonly ml: string;
}

function poursOf(pours: readonly StandardPour[]): PourDraft[] {
  return pours.map((p) => ({ id: newTabId(), ml: String(p.ml) }));
}

/** The rows with an amount in them, as the API takes them. A blank row is not a measure. */
function poursToSave(rows: readonly PourDraft[]): StandardPour[] {
  return rows.filter((r) => r.ml.trim() !== "").map((r) => ({ ml: Number(r.ml) }));
}

interface Draft {
  readonly name: string;
  readonly limits: Record<string, string>;
  readonly wastageReasons: string[];
  readonly prepTabs: PrepTabDraft[];
  readonly aiLimit: string;
  readonly aiReports: boolean;
  readonly houseStyle: string;
  readonly pours: PourDraft[];
  readonly tablets: TabletPolicy;
  readonly mfaRule: MfaRule;
  readonly guestListScope: GuestListScope;
}

function draftOf(orgName: string, s: OrgSettings): Draft {
  return {
    name: orgName,
    // Not `?? 0`. These fields are saved straight back, so a role the API did
    // not name used to be typed into the org's settings as a $0.00 limit —
    // which, unlike a missing one, stops the route falling back and leaves
    // nobody able to approve an order. The API settles this too; this is the
    // second half of the same rule, so the screen cannot show a figure the
    // org is not actually acting under.
    limits: Object.fromEntries(
      LIMIT_ROLES.map((r) => [r, toDollars(s.orderLimitsCents[r] ?? DEFAULT_ORDER_LIMITS[r])]),
    ),
    wastageReasons: [...s.wastageReasons],
    prepTabs: prepTabsOf(s.prepTabs),
    aiLimit: toDollars(s.aiMonthlyLimitCents),
    // Opt-in: anything but a stored `true` is off, because the schedules
    // spend this organisation's AI budget unattended.
    aiReports: s.aiReports === true,
    houseStyle: s.houseStyle ?? "",
    pours: poursOf(s.standardPours ?? DEFAULT_STANDARD_POURS),
    tablets: s.tabletPolicy ?? DEFAULT_TABLET_POLICY,
    mfaRule: s.mfaRule ?? DEFAULT_MFA_RULE,
    guestListScope: s.guestListScope ?? "org",
  };
}

/**
 * The Settings tab — what the prototype hardcoded, now tenant configuration
 * (§4): approval limits, wastage reasons, prep tabs, and the AI cap.
 *
 * Money is entered in dollars and sent in cents. The plan ceiling is shown
 * beside the AI cap and is deliberately not an input: raising it means
 * changing subscription, not typing a bigger number (D10).
 */
export function Settings(p: Props) {
  const app = useApp();
  const isOwner = app.me.org.role === "owner";
  const [draft, setDraft] = useState<Draft>(() => draftOf(p.orgName, p.settings));
  const ceiling = p.settings.aiPlanCeilingCents;
  const proposed = toCents(Number(draft.aiLimit) || 0);
  const overCeiling = proposed > ceiling;
  const used = ceiling > 0 ? Math.min(1, Math.min(proposed, ceiling) / ceiling) : 0;

  const patch = (part: Partial<Draft>) => setDraft((d) => ({ ...d, ...part }));

  return (
    <>
      <div className="org-section">
        <h3>Organisation</h3>
        <div className="org-card">
          <label className="field" style={{ maxWidth: 380 }}>
            <span>Name</span>
            <input type="text" value={draft.name} onChange={(e) => patch({ name: e.target.value })} />
          </label>
        </div>
      </div>

      <div className="org-section">
        <h3>Your Bar OS</h3>
        <div className="org-note">
          What this organisation has. Venox turns features on and off; ask us to change anything here. Turning a
          feature off keeps everything in it, so turning it back on brings it all back.
        </div>
        <FeatureList enabled={app.me.features} />
      </div>

      {hasFeature(app.me, "orders") && (
      <div className="org-section">
        <h3>Order approval limits</h3>
        <div className="org-note">
          The most one person in that role may send to a supplier without somebody else
          approving it. Above the limit an order needs a full sign-in, not a bar PIN.
        </div>
        <div className="org-card">
          <div className="org-grid3">
            {LIMIT_ROLES.map((role) => (
              <label className="field" key={role}>
                <span>{role} ($)</span>
                <input
                  className="num-input"
                  style={{ width: "100%" }}
                  type="number"
                  min={0}
                  step={10}
                  value={draft.limits[role] ?? "0"}
                  onChange={(e) => patch({ limits: { ...draft.limits, [role]: e.target.value } })}
                />
              </label>
            ))}
          </div>
        </div>
      </div>
      )}

      {hasFeature(app.me, "wastage") && (
      <div className="org-section">
        <h3>Wastage reasons</h3>
        <div className="org-note">Offered when somebody logs a spill, a comp or a breakage.</div>
        <ChipList
          items={draft.wastageReasons}
          placeholder="Spill"
          onChange={(wastageReasons) => patch({ wastageReasons })}
        />
      </div>
      )}

      {hasFeature(app.me, "prep") && (
      <div className="org-section">
        <h3>Prep tabs</h3>
        <div className="org-note">
          The tabs prep tasks are grouped under. Use the pencil to rename one in place — its
          tasks move with it. Removing a tab that still has tasks is refused until they are
          moved or cleared.
        </div>
        <PrepTabChips
          tabs={draft.prepTabs}
          placeholder="Opening"
          onChange={(prepTabs) => patch({ prepTabs })}
        />
      </div>
      )}

      {hasFeature(app.me, "guests") && (
        <div className="org-section">
          <h3>Guest list</h3>
          <div className="org-note">
            What a venue manager sees under Reports › Guests. Owners and admins always see every guest. Either way,
            whoever is on the door still recognises a returning guest by their number.
          </div>
          <div className="org-card" style={{ maxWidth: 560 }}>
            <label className="field">
              <span>Venue managers see</span>
              <select
                value={draft.guestListScope}
                onChange={(e) => patch({ guestListScope: e.target.value as GuestListScope })}
              >
                <option value="org">Every guest in the organisation</option>
                <option value="venue">Only guests seen at the venues they manage</option>
              </select>
            </label>
          </div>
        </div>
      )}

      <div className="org-section">
        <h3>Two-step sign-in</h3>
        <div className="org-note">
          A code from an authenticator app, or one sent by email, after the password. Each person sets up their
          own from the account panel; whoever has to and has not is asked to before anything else. Tablets
          unlocked with a PIN are not affected.
          {!isOwner && " Only an owner can change this."}
        </div>
        <div className="org-card" style={{ maxWidth: 560 }}>
          <label className="field">
            <span>Who has to use it</span>
            <select
              value={draft.mfaRule}
              disabled={!isOwner}
              onChange={(e) => patch({ mfaRule: e.target.value as MfaRule })}
            >
              {MFA_CHOICES.map((c) => (
                <option key={c.rule} value={c.rule}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {hasFeature(app.me, "tablets") && (
      <div className="org-section">
        <h3>Tablets</h3>
        <div className="org-note">
          How the venue tablets behave once someone has unlocked them with their PIN. Signing in with a password on
          any device is not affected.
        </div>
        <div className="org-card tablet-policy" style={{ maxWidth: 560 }}>
          <label className="field">
            <span>Lock a tablet after it has been left</span>
            <select
              value={draft.tablets.idleMinutes === null ? "never" : String(draft.tablets.idleMinutes)}
              onChange={(e) =>
                patch({ tablets: { ...draft.tablets, idleMinutes: e.target.value === "never" ? null : Number(e.target.value) } })
              }
            >
              {IDLE_CHOICES.map((m) => (
                <option key={String(m)} value={m === null ? "never" : String(m)}>
                  {m === null ? "Only when someone taps Lock" : `${m} minutes idle`}
                </option>
              ))}
            </select>
          </label>
          <div className="label" style={{ marginTop: 14 }}>Ask for a password, not just a PIN, to…</div>
          <StepUpRow
            label="Approve or send an order over"
            on={draft.tablets.stepUp.orders.on}
            cents={draft.tablets.stepUp.orders.overCents}
            onChange={(on, overCents) => patch({ tablets: { ...draft.tablets, stepUp: { ...draft.tablets.stepUp, orders: { on, overCents } } } })}
          />
          <StepUpRow
            label="Approve wastage over"
            on={draft.tablets.stepUp.wastage.on}
            cents={draft.tablets.stepUp.wastage.overCents}
            onChange={(on, overCents) => patch({ tablets: { ...draft.tablets, stepUp: { ...draft.tablets.stepUp, wastage: { on, overCents } } } })}
          />
          {(
            [
              ["cashupFinalise", "Finalise a cash-up"],
              ["prices", "Change a menu price"],
              ["roles", "Change who can do what (roles and venues)"],
            ] as const
          ).map(([key, label]) => (
            <label className="row tablet-toggle" key={key}>
              <input
                type="checkbox"
                checked={draft.tablets.stepUp[key]}
                onChange={(e) => patch({ tablets: { ...draft.tablets, stepUp: { ...draft.tablets.stepUp, [key]: e.target.checked } } })}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>
      )}

      {hasFeature(app.me, "chat") && (
      <div className="org-section">
        <h3>House style</h3>
        <div className="org-note">
          How this bar writes and serves a drink, in your own words: glassware, dilution, how a garnish
          is described, what does and does not go on the list. The development chat reads it before
          every answer and follows it over anything it works out from the recipe book.
        </div>
        <textarea
          className="house-style"
          aria-label="House style"
          maxLength={4000}
          rows={8}
          placeholder={"Stirred drinks over a block in a rocks glass, 20 mL dilution.\nGarnishes are written as the cut: \"orange coin\", not \"orange peel\".\nNothing over $26 on the main list; no cream drinks after March."}
          value={draft.houseStyle}
          onChange={(e) => patch({ houseStyle: e.target.value })}
        />
        <div className="label" style={{ marginTop: 6 }}>{draft.houseStyle.length} / 4000</div>
      </div>
      )}

      {hasFeature(app.me, "chat") && (
      <div className="org-section">
        <h3>Standard pours</h3>
        <div className="org-note">
          The measures this bar pours to. The development chat may only propose a build in these amounts, so a
          drink it writes can be made with the jiggers behind the bar. Bitters and acids stay in whole dashes, a
          top stays a top, and dilution is not poured, so none of those are held to the table. Remove every row
          to let the chat use any amount.
        </div>
        <div className="org-card pours-table" style={{ maxWidth: 320 }}>
          <div className="pours-row pours-head label">
            <span>Amount (mL)</span>
            <span />
          </div>
          {draft.pours.map((row) => (
            <div className="pours-row" key={row.id}>
              <input
                className="num-input"
                type="number"
                min={1}
                max={1000}
                step={2.5}
                aria-label="Amount in millilitres"
                value={row.ml}
                onChange={(e) => patch({ pours: draft.pours.map((r) => (r.id === row.id ? { ...r, ml: e.target.value } : r)) })}
              />
              <button
                type="button"
                className="btn sm"
                aria-label={`Remove ${row.ml || "this"} mL`}
                onClick={() => patch({ pours: draft.pours.filter((r) => r.id !== row.id) })}
              >
                ×
              </button>
            </div>
          ))}
          {draft.pours.length === 0 && <div className="org-note">No measures: the chat may use any amount.</div>}
          <div className="row" style={{ marginTop: 12, gap: 8 }}>
            <button
              type="button"
              className="btn sm"
              disabled={draft.pours.length >= MAX_STANDARD_POURS}
              onClick={() => patch({ pours: [...draft.pours, { id: newTabId(), ml: "" }] })}
            >
              Add a measure
            </button>
            <button type="button" className="btn sm" onClick={() => patch({ pours: poursOf(DEFAULT_STANDARD_POURS) })}>
              Restore the defaults
            </button>
          </div>
        </div>
      </div>
      )}

      {hasFeature(app.me, "chat") && (
      <div className="org-section">
        <h3>Development chat budget</h3>
        <div className="org-card" style={{ maxWidth: 460 }}>
          <label className="field">
            <span>This organisation's monthly cap ($)</span>
            <input
              className="num-input org-num"
              type="number"
              min={0}
              step={5}
              value={draft.aiLimit}
              onChange={(e) => patch({ aiLimit: e.target.value })}
            />
          </label>
          <div className="meter" aria-hidden="true">
            <span style={{ width: `${used * 100}%` }} />
          </div>
          <div className="kv">
            <span className="k">Plan ceiling</span>
            <span className="v">{money(ceiling / 100)} / month</span>
          </div>
          <div className="org-note">
            {overCeiling
              ? `Your plan allows ${money(ceiling / 100)} a month, so this will be saved as ${money(ceiling / 100)}. Upgrade to go higher.`
              : "You can set any cap up to the plan ceiling yourself. The ceiling itself comes with the subscription."}
          </div>
        </div>
      </div>
      )}

      {hasFeature(app.me, "chat") && (
      <div className="org-section">
        <h3>Written reports</h3>
        <div className="org-note">
          A read of each night, each week and each month, written from the till's own figures and
          what the bar actually poured, and shown on the sales screen beside the period it is
          about. Nobody has to ask for one — they are written after a period closes.
        </div>
        <div className="org-card" style={{ maxWidth: 560 }}>
          <label className="row tablet-toggle">
            <input
              type="checkbox"
              checked={draft.aiReports}
              onChange={(e) => patch({ aiReports: e.target.checked })}
            />
            <span>Write them for this organisation</span>
          </label>
          <div className="org-note">
            {draft.aiReports
              ? "Each report is a model call and comes out of the cap above, so a month over the cap writes no reports until the next one. A venue that did not trade gets none."
              : "Off, so nothing is written and nothing is spent. There is no report for a period that closed while this was off."}
          </div>
        </div>
      </div>
      )}

      <div className="row" style={{ marginTop: 24 }}>
        <button className="btn" onClick={() => setDraft(draftOf(p.orgName, p.settings))} disabled={p.busy === "settings"}>
          Reset
        </button>
        <span className="spacer" />
        <button
          className="btn primary"
          disabled={p.busy === "settings"}
          onClick={() =>
            void p.onSave({
              name: draft.name.trim(),
              orderLimitsCents: Object.fromEntries(
                LIMIT_ROLES.map((r) => [r, toCents(Number(draft.limits[r]) || 0)]),
              ),
              wastageReasons: draft.wastageReasons.map((s) => s.trim()).filter(Boolean),
              prepTabs: draft.prepTabs.map((t) => t.label.trim()).filter(Boolean),
              prepTabRenames: draft.prepTabs
                .filter((t) => t.original !== null && t.original !== t.label.trim() && t.label.trim() !== "")
                .map((t) => ({ from: t.original!, to: t.label.trim() })),
              aiMonthlyLimitCents: proposed,
              ...(hasFeature(app.me, "chat") ? { aiReports: draft.aiReports } : {}),
              houseStyle: draft.houseStyle.trim(),
              ...(hasFeature(app.me, "chat") ? { standardPours: poursToSave(draft.pours) } : {}),
              tabletPolicy: draft.tablets,
              // Only an owner's choice is sent; anyone else keeps what is stored.
              ...(isOwner ? { mfaRule: draft.mfaRule } : {}),
              ...(hasFeature(app.me, "guests") ? { guestListScope: draft.guestListScope } : {}),
            })
          }
        >
          {p.busy === "settings" ? "Saving…" : "Save settings"}
        </button>
      </div>
    </>
  );
}

/** An editable list of short labels, rendered as chips. */
function ChipList(props: {
  readonly items: readonly string[];
  readonly placeholder: string;
  readonly onChange: (items: string[]) => void;
}) {
  const [next, setNext] = useState("");
  const add = () => {
    const value = next.trim();
    if (!value) return;
    if (props.items.some((i) => i.toLowerCase() === value.toLowerCase())) {
      setNext("");
      return;
    }
    props.onChange([...props.items, value]);
    setNext("");
  };
  return (
    <div className="chips">
      {props.items.map((item, i) => (
        <span className="chip assign-chip" key={`${item}-${i}`}>
          {item}
          <button
            aria-label={`Remove ${item}`}
            onClick={() => props.onChange(props.items.filter((_, j) => j !== i))}
          >
            ×
          </button>
        </span>
      ))}
      <input
        type="text"
        value={next}
        placeholder={props.placeholder}
        onChange={(e) => setNext(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          add();
        }}
      />
      <button className="btn sm dashed" onClick={add} disabled={!next.trim()}>
        + Add
      </button>
    </div>
  );
}

/**
 * Prep-tab chips, editable in place — the one list where a name change means
 * more than relabelling: it is `prepTabRenames[].from` → `.to` on save, and
 * the API moves that tab's tasks to match (issue #59).
 *
 * A drop-and-re-add of the same name would look identical to a rename from
 * the request's point of view, which is exactly why this does not do that:
 * each chip keeps the `id` (and `original` name) it was created with for the
 * whole editing session, so `Settings` can tell "renamed" from "removed one,
 * added another" no matter how the label changes in between.
 */
function PrepTabChips(props: {
  readonly tabs: readonly PrepTabDraft[];
  readonly placeholder: string;
  readonly onChange: (tabs: PrepTabDraft[]) => void;
}) {
  const [next, setNext] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  // Committing an edit ends it, which unmounts the input and fires a native
  // blur — this stops that blur from re-running the commit a second time.
  const skipNextBlur = useRef(false);

  const add = () => {
    const value = next.trim();
    if (!value) return;
    if (props.tabs.some((t) => t.label.toLowerCase() === value.toLowerCase())) {
      setNext("");
      return;
    }
    props.onChange([...props.tabs, { id: newTabId(), label: value, original: null }]);
    setNext("");
  };

  const startEdit = (tab: PrepTabDraft) => {
    setEditingId(tab.id);
    setEditValue(tab.label);
  };

  const finishEdit = (commit: boolean) => {
    skipNextBlur.current = true;
    const id = editingId;
    setEditingId(null);
    if (!commit || id === null) return;
    const value = editValue.trim();
    if (!value) return;
    const collides = props.tabs.some(
      (t) => t.id !== id && t.label.toLowerCase() === value.toLowerCase(),
    );
    if (collides) return;
    props.onChange(props.tabs.map((t) => (t.id === id ? { ...t, label: value } : t)));
  };

  return (
    <div className="chips">
      {props.tabs.map((tab) =>
        editingId === tab.id ? (
          <span className="chip assign-chip" key={tab.id}>
            <input
              autoFocus
              className="chip-edit-input"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  finishEdit(true);
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  finishEdit(false);
                }
              }}
              onBlur={() => {
                if (skipNextBlur.current) {
                  skipNextBlur.current = false;
                  return;
                }
                finishEdit(true);
              }}
            />
          </span>
        ) : (
          <span className="chip assign-chip" key={tab.id}>
            {tab.label}
            <button aria-label={`Rename ${tab.label}`} className="edit" onClick={() => startEdit(tab)}>
              ✎
            </button>
            <button
              aria-label={`Remove ${tab.label}`}
              onClick={() => props.onChange(props.tabs.filter((t) => t.id !== tab.id))}
            >
              ×
            </button>
          </span>
        ),
      )}
      <input
        type="text"
        value={next}
        placeholder={props.placeholder}
        onChange={(e) => setNext(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          add();
        }}
      />
      <button className="btn sm dashed" onClick={add} disabled={!next.trim()}>
        + Add
      </button>
    </div>
  );
}

/** One "ask for a password above an amount" setting: a switch and a dollar figure. */
function StepUpRow(props: {
  readonly label: string;
  readonly on: boolean;
  readonly cents: number;
  readonly onChange: (on: boolean, cents: number) => void;
}) {
  return (
    <label className="row tablet-toggle">
      <input type="checkbox" checked={props.on} onChange={(e) => props.onChange(e.target.checked, props.cents)} />
      <span>{props.label}</span>
      <span className="spacer" />
      <span className="label">$</span>
      <input
        className="num-input"
        type="number"
        min={0}
        step={50}
        aria-label={`${props.label} (dollars)`}
        value={props.cents / 100}
        disabled={!props.on}
        onChange={(e) => props.onChange(props.on, Math.max(0, Math.round((Number(e.target.value) || 0) * 100)))}
      />
    </label>
  );
}

/** The organisation's features, on and off, grouped as the menu groups them. Read-only (D14). */
function FeatureList({ enabled }: { readonly enabled: readonly FeatureId[] | undefined }) {
  const on = new Set<string>(enabled ?? FEATURES.map((f) => f.id));
  return (
    <div className="org-card feature-list">
      {FEATURE_AREAS.map((area) => {
        const features = FEATURES.filter((f) => f.area === area.id);
        if (features.length === 0) return null;
        return (
          <div key={area.id} className="feature-area">
            <div className="label">{area.label}</div>
            {features.map((f) => (
              <div key={f.id} className={`feature-row${"parent" in f ? " sub" : ""}`}>
                <span className={on.has(f.id) ? "feature-on" : "feature-off"}>{on.has(f.id) ? "On" : "Off"}</span>
                <span>{f.label}</span>
              </div>
            ))}
          </div>
        );
      })}
      <div className="feature-area">
        <div className="label">Always included</div>
        {ALWAYS_ON.map((name) => (
          <div key={name} className="feature-row">
            <span className="feature-on">On</span>
            <span>{name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
